import { AssetType } from '~/codegen/graphql'
import { toSnakeCase } from 'remeda'

export interface AssetTypeColor {
  bg: string
  bgDark: string
  border: string
  borderDark: string
  text: string
  textDark: string
  hoverBorder: string
  hoverBorderDark: string
  selectedBorder: string
  selectedBorderDark: string
}

export interface AssetTypeConfig {
  label: string
  icon: string
  isChatDefault: boolean
  isSingleFile: boolean
  hasUsage: boolean
  color: AssetTypeColor
  /** Hidden from all web UI surfaces (listings, filters, create, search). */
  hidden?: boolean
}

const COLORS: Record<string, AssetTypeColor> = {
  blue: {
    bg: 'bg-blue-50',
    bgDark: 'dark:bg-[#1e3a5f]',
    border: 'border-blue-100',
    borderDark: 'dark:border-blue-900',
    text: 'text-blue-400',
    textDark: 'dark:text-blue-300',
    hoverBorder: 'hover:border-blue-300 group-hover:border-blue-300',
    hoverBorderDark: 'dark:hover:border-blue-600 dark:group-hover:border-blue-600',
    selectedBorder: 'border-blue-400',
    selectedBorderDark: 'dark:border-blue-500',
  },
  purple: {
    bg: 'bg-purple-50',
    bgDark: 'dark:bg-[#2d1f5e]',
    border: 'border-purple-100',
    borderDark: 'dark:border-purple-900',
    text: 'text-purple-400',
    textDark: 'dark:text-purple-300',
    hoverBorder: 'hover:border-purple-300 group-hover:border-purple-300',
    hoverBorderDark: 'dark:hover:border-purple-600 dark:group-hover:border-purple-600',
    selectedBorder: 'border-purple-400',
    selectedBorderDark: 'dark:border-purple-500',
  },
  amber: {
    bg: 'bg-amber-50',
    bgDark: 'dark:bg-[#4a3520]',
    border: 'border-amber-100',
    borderDark: 'dark:border-amber-900',
    text: 'text-amber-500',
    textDark: 'dark:text-amber-300',
    hoverBorder: 'hover:border-amber-300 group-hover:border-amber-300',
    hoverBorderDark: 'dark:hover:border-amber-600 dark:group-hover:border-amber-600',
    selectedBorder: 'border-amber-400',
    selectedBorderDark: 'dark:border-amber-500',
  },
  fuchsia: {
    bg: 'bg-fuchsia-50',
    bgDark: 'dark:bg-[#3f1049]',
    border: 'border-fuchsia-100',
    borderDark: 'dark:border-fuchsia-900',
    text: 'text-fuchsia-400',
    textDark: 'dark:text-fuchsia-300',
    hoverBorder: 'hover:border-fuchsia-300 group-hover:border-fuchsia-300',
    hoverBorderDark: 'dark:hover:border-fuchsia-600 dark:group-hover:border-fuchsia-600',
    selectedBorder: 'border-fuchsia-400',
    selectedBorderDark: 'dark:border-fuchsia-500',
  },
  gray: {
    bg: 'bg-gray-50',
    bgDark: 'dark:bg-[#363a42]',
    border: 'border-gray-100',
    borderDark: 'dark:border-gray-800',
    text: 'text-gray-400',
    textDark: 'dark:text-gray-300',
    hoverBorder: 'hover:border-gray-300 group-hover:border-gray-300',
    hoverBorderDark: 'dark:hover:border-gray-500 dark:group-hover:border-gray-500',
    selectedBorder: 'border-gray-400',
    selectedBorderDark: 'dark:border-gray-500',
  },
  teal: {
    bg: 'bg-teal-50',
    bgDark: 'dark:bg-[#153a3a]',
    border: 'border-teal-100',
    borderDark: 'dark:border-teal-900',
    text: 'text-teal-500',
    textDark: 'dark:text-teal-300',
    hoverBorder: 'hover:border-teal-300 group-hover:border-teal-300',
    hoverBorderDark: 'dark:hover:border-teal-600 dark:group-hover:border-teal-600',
    selectedBorder: 'border-teal-400',
    selectedBorderDark: 'dark:border-teal-500',
  },
  green: {
    bg: 'bg-green-50',
    bgDark: 'dark:bg-[#1a3a2a]',
    border: 'border-green-100',
    borderDark: 'dark:border-green-900',
    text: 'text-green-400',
    textDark: 'dark:text-green-300',
    hoverBorder: 'hover:border-green-300 group-hover:border-green-300',
    hoverBorderDark: 'dark:hover:border-green-600 dark:group-hover:border-green-600',
    selectedBorder: 'border-green-400',
    selectedBorderDark: 'dark:border-green-500',
  },
}

export const ASSET_TYPE_CONFIG: Record<AssetType, AssetTypeConfig> = {
  [AssetType.Skill]: {
    label: 'Skill',
    icon: 'light:book',
    isChatDefault: true,
    isSingleFile: false,
    hasUsage: true,
    color: COLORS.blue,
  },
  [AssetType.Agent]: {
    label: 'Agent',
    icon: 'light:brain-circuit',
    isChatDefault: true,
    isSingleFile: true,
    hasUsage: true,
    color: COLORS.purple,
  },
  [AssetType.Command]: {
    label: 'Command',
    icon: 'light:rectangle-terminal',
    isChatDefault: true,
    isSingleFile: true,
    hasUsage: true,
    color: COLORS.amber,
  },
  [AssetType.Mcp]: {
    label: 'MCP',
    icon: 'light:circle-nodes',
    isChatDefault: false,
    isSingleFile: false,
    hasUsage: true,
    color: COLORS.fuchsia,
  },
  [AssetType.Hook]: {
    label: 'Hook',
    icon: 'light:gear',
    isChatDefault: false,
    isSingleFile: false,
    hasUsage: false,
    color: COLORS.gray,
  },
  [AssetType.Rule]: {
    label: 'Rule',
    icon: 'light:pen-ruler',
    isChatDefault: true,
    isSingleFile: true,
    hasUsage: false,
    color: COLORS.teal,
  },
  [AssetType.ClaudeCodePlugin]: {
    label: 'Claude Code Plugin',
    icon: 'light:rectangles-mixed',
    isChatDefault: false,
    isSingleFile: false,
    hasUsage: false,
    color: COLORS.green,
  },
  // Desktop app extension — stored and distributed through the vault but
  // surfaces only in the sx app's Extensions screen, never in the web UI.
  [AssetType.AppPlugin]: {
    label: 'Extension',
    icon: 'light:puzzle-piece',
    isChatDefault: false,
    isSingleFile: false,
    hasUsage: false,
    color: COLORS.gray,
    hidden: true,
  },
}

/** Asset types shown in web UI listings and filters (hidden types excluded). */
export const VISIBLE_ASSET_TYPES = Object.entries(ASSET_TYPE_CONFIG)
  .filter(([, config]) => !config.hidden)
  .map(([value]) => value as AssetType)

export function getAssetTypeConfig(type: AssetType = AssetType.Skill) {
  return ASSET_TYPE_CONFIG[type] ?? ASSET_TYPE_CONFIG[AssetType.Skill]
}

export function getAssetTypeLabel(type: AssetType = AssetType.Skill) {
  return ASSET_TYPE_CONFIG[type]?.label ?? type
}

export function getAssetTypeIcon(type: AssetType = AssetType.Skill) {
  return ASSET_TYPE_CONFIG[type]?.icon ?? ASSET_TYPE_CONFIG[AssetType.Skill].icon
}

export function getAssetTypeColor(type: AssetType = AssetType.Skill) {
  return ASSET_TYPE_CONFIG[type]?.color ?? ASSET_TYPE_CONFIG[AssetType.Skill].color
}

export function isAssetTypeChatDefault(type: AssetType = AssetType.Skill) {
  return ASSET_TYPE_CONFIG[type]?.isChatDefault ?? ASSET_TYPE_CONFIG[AssetType.Skill].isChatDefault
}

export function isAssetTypeSingleFile(type: AssetType = AssetType.Skill) {
  return ASSET_TYPE_CONFIG[type]?.isSingleFile ?? ASSET_TYPE_CONFIG[AssetType.Skill].isSingleFile
}

export function getAssetTypeFromString(type: string = ''): AssetType {
  const key = toSnakeCase(type).toUpperCase()
  const values = Object.values(AssetType) as string[]
  if (values.includes(key)) return key as AssetType

  return AssetType.Skill
}
